import React, { useState } from "react";
import "../../Styles/DeepamSchemeTable.css";
import { useNavigate } from "react-router-dom";
import {
  FaFireAlt,
  FaUsers,
  FaCheckCircle,
  FaHourglassHalf,
  FaSearch,
  FaMapMarkerAlt,
  FaFilter,
  FaSyncAlt,
  FaUserCircle,
  FaCalendarAlt,
  FaEdit,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaLeaf,
  FaHeartbeat,
  FaSun,
} from "react-icons/fa";

const INITIAL_DATA = [
  {
    id: 1,
    headOfHousehold: "Yerra Subbamma",
    relation: "W/O Yerra Koteswara Rao",
    rationCard: "AP012345678",
    gasConnection: "GC-88213456",
    agency: "Sri Lakshmi Gas Agency",
    village: "Gudluru",
    mandal: "Gudluru",
    date: "18-06-2026",
    status: "Approved",
  },
  {
    id: 2,
    headOfHousehold: "Pothina Anasuya",
    relation: "W/O Pothina Ramesh",
    rationCard: "AP098765432",
    gasConnection: "GC-77123890",
    agency: "Sri Venkateswara Gas Agency",
    village: "Ulavapadu",
    mandal: "Ulavapadu",
    date: "29-05-2026",
    status: "Pending",
  },
  {
    id: 3,
    headOfHousehold: "Nallamothu Vijaya",
    relation: "W/O Nallamothu Suresh",
    rationCard: "AP056789123",
    gasConnection: "GC-99456712",
    agency: "Bharat Gas Kandukur",
    village: "Kandukur",
    mandal: "Kandukur",
    date: "07-04-2026",
    status: "Approved",
  },
];

const AdminDeepamSchemeTable = () => {
  const navigate = useNavigate();

  const [deepamData, setDeepamData] = useState(INITIAL_DATA);

  const [searchTerm, setSearchTerm] = useState("");
  const [mandalFilter, setMandalFilter] = useState("All Mandals");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);

  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(null);

  /* ===================== SEARCH + FILTER LOGIC ===================== */

  const filteredData = deepamData.filter((item) => {
    const matchesSearch =
      searchTerm.trim() === "" ||
      item.headOfHousehold.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.relation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.rationCard.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.gasConnection.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesMandal =
      mandalFilter === "All Mandals" || item.mandal === mandalFilter;

    const matchesStatus =
      statusFilter === "All Status" || item.status === statusFilter;

    return matchesSearch && matchesMandal && matchesStatus;
  });

  const handleResetFilters = () => {
    setSearchTerm("");
    setMandalFilter("All Mandals");
    setStatusFilter("All Status");
    setCurrentPage(1);
  };

  const totalEntries = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / 10));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * 10;
  const endIndex = startIndex + 10;
  const pageData = filteredData.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  /* ===================== EDIT LOGIC ===================== */

  const openEditModal = (item) => {
    setEditItem(item);
    setEditForm({ ...item });
  };

  const closeEditModal = () => {
    setEditItem(null);
    setEditForm(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSave = (e) => {
    e.preventDefault();
    setDeepamData((prev) =>
      prev.map((item) => (item.id === editForm.id ? { ...editForm } : item)),
    );
    closeEditModal();
  };

  /* ===================== STATS ===================== */

  const totalBeneficiaries = deepamData.length;
  const approvedCount = deepamData.filter(
    (i) => i.status === "Approved",
  ).length;
  const pendingCount = deepamData.filter(
    (i) => i.status === "Pending",
  ).length;
  const approvedPct =
    totalBeneficiaries > 0
      ? ((approvedCount / totalBeneficiaries) * 100).toFixed(1)
      : 0;
  const pendingPct =
    totalBeneficiaries > 0
      ? ((pendingCount / totalBeneficiaries) * 100).toFixed(1)
      : 0;

  return (
    <div className="dp-page">
      {/* ── FIXED BANNER HEADER ── */}
      <div className="dp-header">
        <div className="dp-header-left">
          <div className="dp-header-icon">
            <FaFireAlt />
          </div>
          <div>
            <h2 className="dp-title">Deepam Scheme Beneficiaries</h2>
            <p className="dp-subtitle">
              Manage and track beneficiaries under Deepam scheme
            </p>
          </div>
        </div>

        <button
          className="dp-add-btn"
          onClick={() => navigate("/AdminDashboard/DeepamSchemeForm")}
        >
          <span className="dp-add-plus">+</span> Add New Beneficiary
        </button>
      </div>

      {/* ── SCROLLABLE BODY ── */}
      <div className="dp-scroll-body">
        {/* Stat Cards */}
        <div className="dp-stats-row">
          <div className="dp-stat-card">
            <div className="dp-stat-icon dp-icon-blue">
              <FaUsers />
            </div>
            <div>
              <p className="dp-stat-label">Total Beneficiaries</p>
              <h3 className="dp-stat-value">{totalBeneficiaries}</h3>
              <p className="dp-stat-sub">All time records</p>
            </div>
          </div>

          <div className="dp-stat-card">
            <div className="dp-stat-icon dp-icon-green">
              <FaCheckCircle />
            </div>
            <div>
              <p className="dp-stat-label">Approved Beneficiaries</p>
              <h3 className="dp-stat-value">{approvedCount}</h3>
              <p className="dp-stat-sub dp-sub-green">{approvedPct}% of total</p>
            </div>
          </div>

          <div className="dp-stat-card">
            <div className="dp-stat-icon dp-icon-orange">
              <FaHourglassHalf />
            </div>
            <div>
              <p className="dp-stat-label">Pending Beneficiaries</p>
              <h3 className="dp-stat-value">{pendingCount}</h3>
              <p className="dp-stat-sub dp-sub-orange">{pendingPct}% of total</p>
            </div>
          </div>

          <div className="dp-stat-card">
            <div className="dp-stat-icon dp-icon-red">
              <FaFireAlt />
            </div>
            <div>
              <p className="dp-stat-label">Total Gas Connections</p>
              <h3 className="dp-stat-value">{totalBeneficiaries}</h3>
              <p className="dp-stat-sub dp-sub-red">All time distributed</p>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="dp-filter-section">
          <div className="dp-search-wrapper">
            <FaSearch className="dp-search-icon" />
            <input
              type="text"
              placeholder="Search Beneficiary..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="dp-select-wrapper">
            <FaMapMarkerAlt className="dp-select-icon" />
            <select
              value={mandalFilter}
              onChange={(e) => {
                setMandalFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>All Mandals</option>
              <option>Kandukur</option>
              <option>Gudluru</option>
              <option>Ulavapadu</option>
            </select>
          </div>

          <div className="dp-select-wrapper">
            <FaFilter className="dp-select-icon" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>All Status</option>
              <option>Approved</option>
              <option>Pending</option>
            </select>
          </div>

          <button className="dp-reset-btn" onClick={handleResetFilters}>
            <FaSyncAlt /> Reset Filters
          </button>
        </div>

        {/* Table */}
        <div className="dp-table-wrapper">
          <table className="dp-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Head of Household</th>
                <th>Relation</th>
                <th>Ration Card No.</th>
                <th>Gas Connection No.</th>
                <th>Gas Agency</th>
                <th>Village</th>
                <th>Mandal</th>
                <th>Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {pageData.length > 0 ? (
                pageData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{startIndex + index + 1}</td>
                    <td>
                      <div className="dp-name-cell">
                        <span className="dp-avatar">
                          <FaUserCircle />
                        </span>
                        <span className="dp-name-text">
                          {item.headOfHousehold}
                        </span>
                      </div>
                    </td>
                    <td className="dp-relation-cell">{item.relation}</td>
                    <td>
                      <span className="dp-ration-badge">
                        {item.rationCard}
                      </span>
                    </td>
                    <td>
                      <span className="dp-gas-badge">
                        <FaFireAlt /> {item.gasConnection}
                      </span>
                    </td>
                    <td>{item.agency}</td>
                    <td>
                      <span className="dp-village-cell">
                        <FaMapMarkerAlt /> {item.village}
                      </span>
                    </td>
                    <td>{item.mandal}</td>
                    <td>
                      <span className="dp-date-cell">
                        <FaCalendarAlt /> {item.date}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`dp-status ${
                          item.status === "Approved"
                            ? "dp-approved"
                            : "dp-pending"
                        }`}
                      >
                        {item.status === "Approved" ? (
                          <FaCheckCircle />
                        ) : (
                          <FaHourglassHalf />
                        )}{" "}
                        {item.status}
                      </span>
                    </td>
                    <td className="dp-action-cell">
                      <button
                        className="dp-edit-btn"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit /> Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="dp-no-data">
                    No matching beneficiaries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer / Pagination */}
        <div className="dp-footer">
          <p className="dp-footer-text">
            {totalEntries > 0
              ? `Showing ${startIndex + 1} to ${Math.min(
                  endIndex,
                  totalEntries,
                )} of ${totalEntries} beneficiaries`
              : "Showing 0 beneficiaries"}
          </p>

          <div className="dp-pagination">
            <button
              className="dp-page-btn"
              onClick={() => goToPage(safePage - 1)}
              disabled={safePage === 1}
            >
              <FaChevronLeft />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (page) => (
                <button
                  key={page}
                  className={`dp-page-btn ${
                    safePage === page ? "dp-page-active" : ""
                  }`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ),
            )}

            <button
              className="dp-page-btn"
              onClick={() => goToPage(safePage + 1)}
              disabled={safePage === totalPages}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

        {/* Scheme Info Banner */}
        <div className="dp-scheme-banner">
          <div className="dp-banner-icon-left">
            <FaFireAlt />
          </div>

          <div className="dp-banner-text">
            <h4>Deepam Scheme</h4>
            <p>
              Providing free LPG cylinders to eligible households for a
              better and healthier life.
            </p>
          </div>

          <div className="dp-banner-highlights">
            <span>
              <FaLeaf /> Clean Energy
            </span>
            <span>
              <FaHeartbeat /> Healthy Families
            </span>
            <span>
              <FaSun /> Better Tomorrow
            </span>
          </div>
        </div>
      </div>

      {/* ===================== EDIT MODAL ===================== */}
      {editItem && editForm && (
        <div className="dp-modal-overlay" onClick={closeEditModal}>
          <div
            className="dp-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dp-modal-header">
              <h3>Edit Beneficiary</h3>
              <button className="dp-modal-close" onClick={closeEditModal}>
                <FaTimes />
              </button>
            </div>

            <form className="dp-modal-form" onSubmit={handleEditSave}>
              <div className="dp-form-row">
                <label>Head of Household</label>
                <input
                  type="text"
                  value={editForm.headOfHousehold}
                  onChange={(e) =>
                    handleEditChange("headOfHousehold", e.target.value)
                  }
                  required
                />
              </div>

              <div className="dp-form-row">
                <label>Relation</label>
                <input
                  type="text"
                  value={editForm.relation}
                  onChange={(e) =>
                    handleEditChange("relation", e.target.value)
                  }
                  required
                />
              </div>

              <div className="dp-form-grid">
                <div className="dp-form-row">
                  <label>Ration Card No.</label>
                  <input
                    type="text"
                    value={editForm.rationCard}
                    onChange={(e) =>
                      handleEditChange("rationCard", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="dp-form-row">
                  <label>Gas Connection No.</label>
                  <input
                    type="text"
                    value={editForm.gasConnection}
                    onChange={(e) =>
                      handleEditChange("gasConnection", e.target.value)
                    }
                    required
                  />
                </div>
              </div>

              <div className="dp-form-row">
                <label>Gas Agency</label>
                <input
                  type="text"
                  value={editForm.agency}
                  onChange={(e) =>
                    handleEditChange("agency", e.target.value)
                  }
                  required
                />
              </div>

              <div className="dp-form-grid">
                <div className="dp-form-row">
                  <label>Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) =>
                      handleEditChange("status", e.target.value)
                    }
                  >
                    <option>Approved</option>
                    <option>Pending</option>
                  </select>
                </div>

                <div className="dp-form-row">
                  <label>Village</label>
                  <input
                    type="text"
                    value={editForm.village}
                    onChange={(e) =>
                      handleEditChange("village", e.target.value)
                    }
                    required
                  />
                </div>
              </div>

              <div className="dp-form-grid">
                <div className="dp-form-row">
                  <label>Mandal</label>
                  <select
                    value={editForm.mandal}
                    onChange={(e) =>
                      handleEditChange("mandal", e.target.value)
                    }
                  >
                    <option>Kandukur</option>
                    <option>Gudluru</option>
                    <option>Ulavapadu</option>
                  </select>
                </div>

                <div className="dp-form-row">
                  <label>Date</label>
                  <input
                    type="text"
                    placeholder="DD-MM-YYYY"
                    value={editForm.date}
                    onChange={(e) =>
                      handleEditChange("date", e.target.value)
                    }
                    required
                  />
                </div>
              </div>

              <div className="dp-modal-actions">
                <button
                  type="button"
                  className="dp-modal-cancel-btn"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button type="submit" className="dp-modal-save-btn">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDeepamSchemeTable;